import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Search } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, errMsg } from "@/lib/api";
import { LEAD_STATUSES, SERVICE_FILTER_OPTIONS, STATUS_COLORS } from "@/data/content";

const selectCls = "rounded-xl border border-white/10 bg-[#0F1420] px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-violet-500";

export default function AdminLeads() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [service, setService] = useState("");

    const load = () => {
        setLoading(true);
        const params = new URLSearchParams();
        if (search) params.set("search", search);
        if (status) params.set("status", status);
        if (service) params.set("service", service);
        api.get(`/admin/inquiries?${params.toString()}`)
            .then((r) => setItems(r.data))
            .catch((e) => toast.error(errMsg(e)))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, [status, service]); // eslint-disable-line react-hooks/exhaustive-deps

    const exportCsv = async () => {
        try {
            const res = await api.get("/admin/inquiries/export", { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = "inquiries.csv";
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(errMsg(e));
        }
    };

    return (
        <div data-testid="admin-leads-page">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="font-display text-2xl font-bold text-white">Inquiries</h1>
                    <p className="mt-1 text-sm text-slate-400">{items.length} lead{items.length === 1 ? "" : "s"} shown</p>
                </div>
                <button onClick={exportCsv} data-testid="admin-export-csv-button"
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-slate-200 transition-colors hover:border-cyan-400/60 hover:text-white">
                    <Download className="h-4 w-4" /> Export CSV
                </button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); load(); }} className="mt-6 flex flex-wrap gap-3">
                <div className="relative min-w-56 flex-1">
                    <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                    <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-leads-search-input"
                        placeholder="Search name, email, company, reference…"
                        className="w-full rounded-xl border border-white/10 bg-[#0F1420] py-2.5 pl-10 pr-4 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500" />
                </div>
                <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-leads-status-filter" className={selectCls}>
                    <option value="">All statuses</option>
                    {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={service} onChange={(e) => setService(e.target.value)} data-testid="admin-leads-service-filter" className={selectCls}>
                    <option value="">All services</option>
                    {SERVICE_FILTER_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button type="submit" data-testid="admin-leads-search-button"
                    className="rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-violet-500">Search</button>
            </form>

            <div className="mt-6 overflow-x-auto rounded-2xl border border-white/10 bg-hive-card">
                {loading ? (
                    <p className="p-10 text-center text-sm text-slate-500">Loading…</p>
                ) : items.length === 0 ? (
                    <p className="p-10 text-center text-sm text-slate-500" data-testid="admin-leads-empty">No inquiries match these filters.</p>
                ) : (
                    <table className="w-full min-w-[720px] text-left text-sm" data-testid="admin-leads-table">
                        <thead>
                            <tr className="border-b border-white/10 text-xs uppercase tracking-wider text-slate-500">
                                <th className="px-5 py-3.5 font-semibold">Reference</th>
                                <th className="px-5 py-3.5 font-semibold">Name</th>
                                <th className="px-5 py-3.5 font-semibold">Service</th>
                                <th className="px-5 py-3.5 font-semibold">Submitted</th>
                                <th className="px-5 py-3.5 font-semibold">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {items.map((i) => (
                                <tr key={i.id} className="hover:bg-white/5 transition-colors">
                                    <td className="px-5 py-3.5">
                                        <Link to={`/admin/leads/${i.id}`} data-testid={`lead-row-${i.reference}`} className="font-mono2 text-xs text-violet-300 hover:text-violet-200">
                                            {i.reference}
                                        </Link>
                                    </td>
                                    <td className="px-5 py-3.5">
                                        <p className="font-semibold text-white">{i.full_name}</p>
                                        <p className="text-xs text-slate-500">{i.email}{i.company ? ` · ${i.company}` : ""}</p>
                                    </td>
                                    <td className="px-5 py-3.5 text-slate-300">{i.service}</td>
                                    <td className="px-5 py-3.5 text-xs text-slate-400">{new Date(i.created_at).toLocaleString()}</td>
                                    <td className="px-5 py-3.5">
                                        <span className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${STATUS_COLORS[i.status] || ""}`} data-testid={`lead-status-${i.reference}`}>{i.status}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            <p className="mt-4 text-[11px] text-slate-600">API base: {API_BASE}</p>
        </div>
    );
}
